import * as Highcharts from 'highcharts';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ChartProps,
  filteredDataMineIndex,
} from './ComparisonBetweenIndexAndAsset';
import { renderToStaticMarkup } from 'react-dom/server';
import HighchartsReact from 'highcharts-react-official';
import { marker } from 'components/features/utils/chartUtils';
import { TimeSeriesDatum } from 'components/features/home/assetStuffing/AssetStuffing';
import { IconOnChart } from 'components/features/home/assetStuffing/AssetStuffingChart';

export const Chart: React.FC<ChartProps> = ({
  data,
  header,
  colors,
  selectedHeaders,
  changeVisibility,
  xAxisInfo,
}) => {
  const [icons, setIcons] = useState<IconOnChart[]>([]);
  const [toolTipPosition, setToolTipPosition] = useState<{
    x: number;
    y: number;
    width: number;
    date: Date | null;
  }>({ x: 0, y: 0, width: 0, date: null });
  const [showToolTip, setShowToolTip] = useState<boolean>(false);

  const signs = useMemo(
    () =>
      data.map(it => {
        return { value: it[1], date: it[0] } as TimeSeriesDatum;
      }),
    [data]
  );

  const [options, setOptions] = useState<Highcharts.Options>({
    credits: {
      enabled: false,
    },
    xAxis: xAxisInfo,
    subtitle: {
      text: '',
    },
    title: {
      text: '',
    },
    yAxis: {
      opposite: true,
      title: { text: '' },
      labels: {
        formatter: function(): string {
          return typeof this.value === 'number' ? `${this.value * 100}%` : '';
        },
      },
    },
    plotOptions: {
      series: { ...marker, turboThreshold: 15000 },
    },
    tooltip: {
      borderColor: 'transparent',
      useHTML: true,
      outside: true,
      headerFormat: renderToStaticMarkup(
        <span className="tooltip-header">{'{point.x}'}</span>
      ),
      pointFormatter: function(): string {
        const Tooltip: React.FC<{
          name: string;
          color: string;
          value: number;
        }> = ({ name, color, value }) => {
          return (
            <div className={`tooltip ${''}`}>
              <div
                className="tooltip-point"
                style={{ backgroundColor: color }}
              />
              <span className="tooltip-label">{name}:</span>
              <span className="tooltip-value">
                {Math.round(value * 1000) / 10}%
              </span>
            </div>
          );
        };
        return renderToStaticMarkup(
          <Tooltip
            name={this.name}
            color={this.color?.toString() ?? ''}
            value={this.y ?? 0}
          />
        );
      },
      shared: true,
    },
    legend: { enabled: false },
    exporting: {
      enabled: false,
    },
  });
  const ref = useRef<HighchartsReact.RefObject>(null);

  useEffect(() => {
    setOptions(prev => {
      return {
        ...prev,
        series: getArrangeChartData(
          data,
          header,
          selectedHeaders,
          colors,
          changeVisibility
        ),
        chart: {
          height: 250,
          spacingLeft: 0,
          events: {
            render: function() {
              if (!ref.current) return;
              const chart = ref.current.chart;
              drawIcons({ chart, icons, signs });
            },
          },
        },
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, header, colors, selectedHeaders, changeVisibility]);

  const drawIcons = ({
    chart,
    icons,
    signs,
  }: {
    chart: Highcharts.Chart;
    icons: IconOnChart[];
    signs: TimeSeriesDatum[];
  }): void => {
    const iconSize = 15;
    const margin = 5;
    icons.forEach(({ icon }) => {
      if (icon.renderer) icon.destroy();
    });

    const xAxis = chart.xAxis[0];

    const signSeries = signs
      .map((it, index) => {
        return {
          date: it.date,
          value: it.value,
          index: index,
        };
      })
      .filter(it => it.date && it.value);

    signSeries.forEach(({ date, index }) => {
      const x = xAxis.toPixels(index, true) - 0.5 * iconSize;
      if (!chart.isInsidePlot(x, chart.plotTop)) return;
      const icon = chart.renderer
        .image(
          '/static/images/icon/icon-info.svg',
          x,
          chart.plotTop + chart.plotHeight - 7,
          iconSize,
          iconSize
        )
        .add();
      icon.toFront();
      chart.tooltip.getLabel().toFront();
      const tooltipWidth = 150;
      let tx = x - tooltipWidth - margin;
      if (!chart.isInsidePlot(tx, chart.plotTop)) {
        tx = x + iconSize + margin;
      }

      icon.element.addEventListener('mouseover', () => {
        setToolTipPosition({
          x: tx,
          y: chart.chartHeight - chart.plotTop - chart.plotHeight,
          width: tooltipWidth,
          date: date,
        });
        setShowToolTip(true);
      });
      icon.element.addEventListener('mouseout', () => {
        setShowToolTip(false);
      });

      icons.push({ icon });
      setIcons(icons);
    });
  };

  const Tip = ({
    pos,
    show,
  }: {
    pos: { x: number; y: number; width: number; date: Date | null };
    show: boolean;
  }) => {
    return (
      <div
        className="p-2 absolute bg-white rounded-md shadow-md text-xs w-1/2 border border-gray-300"
        style={{
          display: show ? 'block' : 'none',
          zIndex: 999,
          left: pos.x,
          bottom: pos.y,
          maxWidth: pos.width,
        }}>
        <div className="flex flex-row">
          <div
            className="icon-info text-sm"
            id="icon-info"
            style={{ color: '#94A3B8' }}></div>
          <div className="ml-1 text-1.5xs">
            <div>
              集計が正常に完了しなかった可能性があるためこのデータは参考値です。
            </div>
            <div className="mt-1">
              {pos.date !== null
                ? `${pos.date.getFullYear()}/${pos.date.getMonth() +
                    1}/${pos.date.getDate()}`
                : '----/--/--'}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="relative">
      <HighchartsReact highcharts={Highcharts} options={options} ref={ref} />
      <Tip pos={toolTipPosition} show={showToolTip} />
    </div>
  );
};

const getArrangeChartData = (
  data: ChartProps['data'],
  header: ChartProps['header'],
  selectedHeaderNames: string[],
  colors: string[],
  changeVisibility_: (header: string, visibility: boolean) => void
): {
  type: 'line';
  name: string;
  data: Highcharts.SeriesLineOptions['data'];
  color: string;
  marker?: { enabled: false };
  events?: Highcharts.SeriesEventsOptionsObject;
}[] => {
  return data[0]
    .map((v, k) => k)
    .flatMap((v, i) => {
      const getStringFromHeader = (header_: typeof header, i: number) => {
        return header_.slice(1)[i - 1] as string;
      };
      const getValueFromData = (datum: typeof data[number], i: number) => {
        return datum.slice(1)[i - 1] as number | null;
      };
      if (i === 0 || i === 1) return [];
      if (i === filteredDataMineIndex)
        return [
          {
            type: 'line',
            name: getStringFromHeader(header, i - 1),
            data: data.map((datum, di, darr) => ({
              name: getStringFromHeader(header, i - 1),
              y: getValueFromData(datum, i),
              ...(di === darr.length - 1
                ? {
                    dataLabels: {
                      enabled: true,
                      formatter: function(_): string {
                        return this.y !== undefined
                          ? `${Math.round(this.y * 100)}%`
                          : '';
                      },
                      className: 'stick-out-data-label comparison-chart',
                      useHTML: true,
                      padding: 0,
                      crop: false,
                      color: 'white',
                    },
                  }
                : {}),
            })),
            color: colors[i - 1],
            lineWidth: 3,
            marker: { enabled: false },
            visible: selectedHeaderNames.includes(
              getStringFromHeader(header, i - 1)
            ),
          },
        ];
      return [
        {
          type: 'line',
          name: getStringFromHeader(header, i - 1),
          data: data.map(datum => {
            const d = {
              name: getStringFromHeader(header, i - 1),
              y: getValueFromData(datum, i),
            };
            return d;
          }),
          color: colors[i - 2],
          marker: { enabled: false },
          lineWidth: 2,
          visible: selectedHeaderNames.includes(
            getStringFromHeader(header, i - 1)
          ),
          events: {
            legendItemClick: function() {
              changeVisibility_(
                getStringFromHeader(header, i - 1),
                !this.visible
              );
            },
          },
          connectNulls: true,
        },
      ];
    });
};
